import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PAIN_POINTS = [
  {
    id: 1,
    icon: 'ri-money-dollar-circle-line',
    tag: 'Vấn đề #1',
    title: 'Cho thuê giá thấp, không dám tăng',
    desc: 'Tài sản đang cho thuê nhưng giá thuê thấp hơn thị trường 20–40%. Chủ nhà sợ mất khách, không dám tăng giá — trong khi tài sản tương đương ở cùng khu vực đang thu gấp đôi.',
    stat: '40%',
    statLabel: 'chủ nhà đang cho thuê dưới giá thị trường',
    color: 'from-[#004D4D] to-[#003333]',
    accentColor: '#C5A059',
    solution: 'Tái định vị mô hình khai thác, tăng giá thuê 30–60% mà không mất khách',
    solutionIcon: 'ri-arrow-up-circle-line',
    ctaPath: '/dich-vu/van-hanh',
    ctaLabel: 'Xem Dịch Vụ Vận Hành',
  },
  {
    id: 2,
    icon: 'ri-home-2-line',
    tag: 'Vấn đề #2',
    title: 'Nhà bỏ trống, tiền vẫn chảy ra',
    desc: 'Nhà đang bỏ trống hoặc cho thuê cầm chừng. Mỗi tháng vẫn phải trả lãi ngân hàng, phí quản lý, điện nước — trong khi tài sản không tạo ra đồng nào.',
    stat: '3–5tr',
    statLabel: 'chi phí cố định mỗi tháng dù nhà bỏ trống',
    color: 'from-[#8B6914] to-[#6B4F0E]',
    accentColor: '#F5D78E',
    solution: 'Chuyển đổi mô hình khai thác, tạo dòng tiền dương ngay trong 60–90 ngày',
    solutionIcon: 'ri-building-2-line',
    ctaPath: '/dich-vu/xay-cam-ket-thue',
    ctaLabel: 'Xem Dịch Vụ Xây',
  },
  {
    id: 3,
    icon: 'ri-tools-line',
    tag: 'Vấn đề #3',
    title: 'Muốn bán nhưng không ai trả đúng giá',
    desc: 'Nhà đã rao bán 6–12 tháng nhưng không có người mua đúng giá. Tài sản xuống cấp, hình ảnh kém hấp dẫn — người mua trả thấp hơn kỳ vọng 15–25%.',
    stat: '25%',
    statLabel: 'giá trị bị mất khi bán nhà chưa được tối ưu',
    color: 'from-[#5C3D2E] to-[#3D2519]',
    accentColor: '#E8C49A',
    solution: 'Sửa đúng điểm, đúng ngân sách — tăng giá bán 20–35% so với hiện trạng',
    solutionIcon: 'ri-hammer-line',
    ctaPath: '/dich-vu/sua-cam-ket-ban',
    ctaLabel: 'Xem Dịch Vụ Sửa',
  },
];

function PainCard({ p }: { p: typeof PAIN_POINTS[0] }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div
      className="rounded-3xl overflow-hidden border border-neutral-100 hover:-translate-y-1 transition-transform duration-300 cursor-pointer group/card"
      onClick={() => setOpen(v => !v)}
    >
      {/* Header — always visible */}
      <div className={`bg-gradient-to-br ${p.color} p-6 text-white relative overflow-hidden`}>
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1.5">
              <i className={`${p.icon} text-sm`} />
              <span className="text-xs font-heading font-bold tracking-wide">{p.tag}</span>
            </div>
            <i className={`ri-arrow-down-s-line text-white/60 text-xl transition-transform duration-200 ${open ? 'rotate-180' : ''} md:group-hover/card:rotate-180`} />
          </div>
          <h3 className="font-heading font-extrabold text-lg leading-tight mb-3">
            {p.title}
          </h3>
          {/* Stat — compact */}
          <div className="flex items-center gap-3">
            <div className="font-heading font-extrabold text-3xl text-white">{p.stat}</div>
            <div className="text-white/60 text-xs font-sans leading-snug">{p.statLabel}</div>
          </div>
        </div>
      </div>

      {/* Expandable content */}
      <div className="bg-white">
        {/* Mobile toggle */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${open ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="p-6 border-t border-neutral-100">
            <p className="text-neutral-600 text-sm font-sans leading-relaxed mb-4">{p.desc}</p>
            <div className="bg-[#E6F0F0] rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <i className={`${p.solutionIcon} text-[#004D4D] text-sm`} />
                <span className="text-[10px] font-heading font-bold text-[#004D4D] uppercase tracking-wide">Giải Pháp</span>
              </div>
              <p className="font-heading font-semibold text-[#004D4D] text-sm">{p.solution}</p>
            </div>
            <div className="space-y-2.5 mb-4">
              {[
                { step: '01', text: 'Bắt Mạch tài sản — phân tích hiện trạng miễn phí' },
                { step: '02', text: 'Đề xuất phương án tối ưu phù hợp mục tiêu của bạn' },
                { step: '03', text: 'Triển khai và theo dõi kết quả thực tế' },
              ].map((s) => (
                <div key={s.step} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center bg-[#004D4D] rounded-full flex-shrink-0 mt-0.5">
                    <span className="text-white font-heading font-bold text-[9px]">{s.step}</span>
                  </div>
                  <p className="text-neutral-500 text-xs font-sans leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); navigate(p.ctaPath); }}
              className="w-full bg-[#004D4D] hover:bg-[#003333] text-white font-heading font-bold text-xs px-4 py-2.5 rounded-full transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
            >
              {p.ctaLabel} <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>

        {/* Desktop hover */}
        <div className="hidden md:block overflow-hidden max-h-0 opacity-0 group-hover/card:max-h-[400px] group-hover/card:opacity-100 transition-all duration-300">
          <div className="p-6 border-t border-neutral-100">
            <p className="text-neutral-600 text-sm font-sans leading-relaxed mb-4">{p.desc}</p>
            <div className="bg-[#E6F0F0] rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <i className={`${p.solutionIcon} text-[#004D4D] text-sm`} />
                <span className="text-[10px] font-heading font-bold text-[#004D4D] uppercase tracking-wide">Giải Pháp</span>
              </div>
              <p className="font-heading font-semibold text-[#004D4D] text-sm">{p.solution}</p>
            </div>
            <div className="space-y-2.5 mb-4">
              {[
                { step: '01', text: 'Bắt Mạch tài sản — phân tích hiện trạng miễn phí' },
                { step: '02', text: 'Đề xuất phương án tối ưu phù hợp mục tiêu của bạn' },
                { step: '03', text: 'Triển khai và theo dõi kết quả thực tế' },
              ].map((s) => (
                <div key={s.step} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center bg-[#004D4D] rounded-full flex-shrink-0 mt-0.5">
                    <span className="text-white font-heading font-bold text-[9px]">{s.step}</span>
                  </div>
                  <p className="text-neutral-500 text-xs font-sans leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); navigate(p.ctaPath); }}
              className="w-full bg-[#004D4D] hover:bg-[#003333] text-white font-heading font-bold text-xs px-4 py-2.5 rounded-full transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
            >
              {p.ctaLabel} <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PainPointsSection() {
  const navigate = useNavigate();

  return (
    <section id="pain-points" className="py-20 md:py-28 bg-[#F8F6F2]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-[#C5A059] text-xs font-heading font-bold tracking-widest uppercase mb-4">
            VẤN ĐỀ CHỦ NHÀ HAY GẶP
          </span>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#002626] leading-tight mb-4">
            Tài sản của bạn đang rơi vào<br />
            <span className="text-[#C5A059]">tình huống nào?</span>
          </h2>
          <p className="text-neutral-500 text-base font-sans max-w-xl mx-auto leading-relaxed">
            3 vấn đề phổ biến nhất mà chủ nhà phố nội đô TP.HCM đang gặp phải — và cách UPLIVING giải quyết từng cái
          </p>
        </div>

        {/* 3 Cards — always visible */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {PAIN_POINTS.map((p) => (
            <PainCard key={p.id} p={p} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate('/bat-mach-tai-san')}
            className="flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#D4B575] text-[#002626] font-heading font-bold text-sm px-8 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-stethoscope-line" />
            Bắt Mạch Miễn Phí
          </button>
          <button
            onClick={() => navigate('/du-an')}
            className="flex items-center justify-center gap-2 border border-neutral-300 hover:border-[#004D4D] text-neutral-600 hover:text-[#004D4D] font-heading font-semibold text-sm px-8 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-eye-line" />
            Xem Dự Án Thực Tế
          </button>
        </div>
      </div>
    </section>
  );
}
